import { useState, useRef, useEffect } from "react";
import { Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import FaytexFAQ from "@/components/FaytexFAQ";
import CycleCalculator from "@/components/CycleCalculator";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUGGESTIONS = [
  "Is it normal to have irregular periods at 17?",
  "How often should I change my pad?",
  "What's the difference between PMS and PMDD?",
  "Can I exercise during my period?",
  "Why are my cramps worse this month?",
];

export default function FactOrFaytex() {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi sis! I'm 'Fact or Faytex?' — your friendly, judgement-free guide to menstrual health. Ask me anything about your cycle, cramps, hygiene, or even the myths your auntie told you. I'm here for it.",
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  async function send(text) {
    const userText = (text ?? input).trim();
    if (!userText || sending) return;
    setInput("");
    setSending(true);
    setMessages((m) => [...m, { role: "user", content: userText }, { role: "assistant", content: "" }]);

    try {
      const resp = await fetch(`${API}/chat/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: userText }),
      });
      if (!resp.ok || !resp.body) {
        throw new Error("Network error");
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const ev of events) {
          const line = ev.replace(/^data:\s?/, "").trim();
          if (!line) continue;
          try {
            const obj = JSON.parse(line);
            if (obj.type === "session" && obj.session_id) {
              setSessionId(obj.session_id);
            } else if (obj.type === "delta") {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + obj.content };
                return copy;
              });
            } else if (obj.type === "error") {
              toast.error("Something went wrong. Please try again.");
            }
          } catch (e) {
            // ignore parse errors
          }
        }
      }
    } catch (e) {
      toast.error("Could not reach the assistant. Please try again.");
      setMessages((m) => {
        const copy = [...m];
        // remove empty assistant placeholder
        if (copy[copy.length - 1].role === "assistant" && !copy[copy.length - 1].content) copy.pop();
        return copy;
      });
    } finally {
      setSending(false);
    }
  }

  return (
    <div data-testid="fact-or-faytex-page" className="bg-faytex-pinkLight/40 min-h-screen">
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 pb-10 text-center">
        <span className="inline-flex items-center gap-2 rounded-full bg-faytex-pink/10 text-faytex-pinkHover px-4 py-1.5 text-xs uppercase tracking-widest font-medium">
          <Sparkles className="w-3.5 h-3.5" /> Clinically trusted • Judgement-free
        </span>
        <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl mt-5 tracking-tighter">
          Fact <span className="text-faytex-pink">or</span> Faytex?
        </h1>
        <p className="mt-4 text-faytex-muted max-w-xl mx-auto">
          Friendly answers to the menstrual-health questions you have always wanted to ask — powered by AI, grounded in care.
        </p>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="bg-white rounded-3xl shadow-hover border border-gray-100 overflow-hidden">
          <div className="bg-faytex-pink text-white px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-mint-400 bg-faytex-mint" />
              <span className="font-heading text-xl">Fact or Faytex?</span>
            </div>
            <span className="text-[11px] uppercase tracking-widest opacity-90">AI Assistant</span>
          </div>

          <div ref={scrollRef} data-testid="chat-scroll" className="h-[520px] overflow-y-auto p-6 space-y-4 bg-white">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-msg-${m.role}-${i}`}
                  className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-gray-100 text-faytex-ink rounded-tr-sm"
                      : "bg-faytex-pinkLight text-faytex-pinkHover rounded-tl-sm"
                  }`}
                >
                  {m.content || (sending && i === messages.length - 1 ? <TypingDots /> : "")}
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-gray-100 p-4 bg-white">
            <div className="flex flex-wrap gap-2 mb-3">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  data-testid={`suggestion-${s.substring(0, 12).replace(/\s/g, '-').toLowerCase()}`}
                  onClick={() => send(s)}
                  disabled={sending}
                  className="text-xs px-3 py-1.5 rounded-full bg-faytex-pinkLight text-faytex-pinkHover hover:bg-faytex-pink hover:text-white transition-colors disabled:opacity-50"
                >
                  {s}
                </button>
              ))}
            </div>
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex items-center gap-2 bg-gray-50 rounded-full px-4 py-2 border border-gray-100 focus-within:border-faytex-pink"
            >
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask anything about menstrual health…"
                className="flex-1 bg-transparent outline-none text-sm py-2"
                disabled={sending}
              />
              <button
                data-testid="chat-send-button"
                type="submit"
                disabled={sending || !input.trim()}
                className="w-10 h-10 rounded-full bg-faytex-pink hover:bg-faytex-pinkHover text-white flex items-center justify-center disabled:opacity-50"
                aria-label="Send"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
            <p className="mt-3 text-[11px] text-faytex-muted text-center">
              For medical concerns, please consult a healthcare professional.
            </p>
          </div>
        </div>
      </section>

      <FaytexFAQ />

      <CycleCalculator />
    </div>
  );
}

function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="w-1.5 h-1.5 rounded-full bg-faytex-pink animate-pulse" />
      <span className="w-1.5 h-1.5 rounded-full bg-faytex-pink animate-pulse [animation-delay:0.15s]" />
      <span className="w-1.5 h-1.5 rounded-full bg-faytex-pink animate-pulse [animation-delay:0.3s]" />
    </span>
  );
}
